import React, { useState, FormEvent } from 'react';
import { FurnitureFilters } from '../../services/furniture';
import { NEIGHBORHOODS } from '../../constants/neighborhoods';
import { useTranslation } from 'react-i18next';

interface FurnitureFilterProps {
  onFilterChange: (filters: Partial<FurnitureFilters>) => void;
  initialSearchTerm?: string;
  initialCategory?: string;
  categories: string[];
  conditions: string[];
}

const FurnitureFilter: React.FC<FurnitureFilterProps> = ({
  onFilterChange,
  initialSearchTerm = '',
  initialCategory = '',
  categories,
  conditions
}) => {
  const { i18n, t } = useTranslation();
  const [searchInput, setSearchInput] = useState(initialSearchTerm);
  const [category, setCategory] = useState(initialCategory);
  const [condition, setCondition] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [neighborhood, setNeighborhood] = useState('');

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    onFilterChange({ search: searchInput });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setCategory(value);
    onFilterChange({ category: value });
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setCondition(value);
    onFilterChange({ condition: value });
  };

  const handlePriceRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setPriceRange(value);
    if (value) {
      const [min, max] = value.split('-').map(Number);
      onFilterChange({ minPrice: min, maxPrice: max || undefined });
    } else {
      onFilterChange({ minPrice: undefined, maxPrice: undefined });
    }
  };

  const clearAllFilters = () => {
    setSearchInput('');
    setCategory('');
    setCondition('');
    setPriceRange('');
    setNeighborhood('');
    onFilterChange({
      search: '',
      category: '',
      condition: '',
      neighborhood: '',
      minPrice: undefined,
      maxPrice: undefined
    });
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearchSubmit} className="w-full">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('furniture.listing.searchPlaceholder')}
            className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                onFilterChange({ search: '' });
              }}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <svg className="h-5 w-5 text-gray-400 hover:text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
        {/* Category Filter */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700">{t('furniture.listing.byCategory')}</label>
          <select
            id="category"
            className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            value={category}
            onChange={handleCategoryChange}
          >
            <option value="">{t('furniture.listing.allCategories')}</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{t(`home.categories.${cat}`)}</option>
            ))}
          </select>
        </div>

        {/* Condition Filter */}
        <div>
          <label htmlFor="condition" className="block text-sm font-medium text-gray-700">{t('furniture.listing.byCondition')}</label>
          <select
            id="condition"
            className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            value={condition}
            onChange={handleConditionChange}
          >
            <option value="">{t('furniture.listing.allConditions')}</option>
            {conditions.map((cond) => (
              <option key={cond} value={cond}>{t(`furniture.condition.${cond}`)}</option>
            ))}
          </select>
        </div>

        {/* Price Range Filter */}
        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-700">{t('furniture.listing.priceRange')}</label>
          <select
            id="price"
            className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            value={priceRange}
            onChange={handlePriceRangeChange}
          >
            <option value="">{t('furniture.listing.allPrices')}</option>
            <option value="0-100">{t('furniture.listing.priceUnder100')}</option>
            <option value="100-200">$100 - $200</option>
            <option value="200-300">$200 - $300</option>
            <option value="300-500">$300 - $500</option>
            <option value="500-1000">$500 - $1000</option>
            <option value="1000-">$1000+</option>
          </select>
        </div>

        {/* Neighborhood Filter */}
        <div>
          <label htmlFor="neighborhood" className="block text-sm font-medium text-gray-700">{t('furniture.listing.byNeighborhood')}</label>
          <select
            id="neighborhood"
            className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            value={neighborhood}
            onChange={(e) => {
              setNeighborhood(e.target.value);
              onFilterChange({ neighborhood: e.target.value });
            }}
          >
            <option value="">{t('furniture.listing.allNeighborhoods')}</option>
            {NEIGHBORHOODS.map((item) => (
              <option key={item.slug} value={item.slug}>
                {i18n.language.startsWith('zh') ? item.zh : item.en}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button */}
        <div className="flex items-end">
          <button
            type="button"
            className="w-full bg-gray-200 hover:bg-gray-300 py-2 px-4 rounded-md text-sm font-medium text-gray-700"
            onClick={clearAllFilters}
          >
            {t('furniture.listing.clearFilters')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FurnitureFilter;
